import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Input, Button, Popconfirm } from 'antd';
import Form from '../../components/Form';
import { showNotification, ERROR_TYPE } from '../../components/Notification';
import styles from './index.module.scss';
import { LAYOUT_0_24, VALIDATE_MESSAGES } from '../../constants/form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
  getCurrentChild,
  selectCurrentChild,
  updateChild,
} from '../../store/reducers/homeSlice';
import { RootState } from '../../store/reducers';
import moment from 'moment';
import AntdDatePicker from '../../components/Input/DatePicker';
import { deleteParent, getGiftApi, getLogEntriesApi } from '../../api';
import WealthieBreadCrumb from '../../components/BreadCrumb';
import withLoading from '../../helpers/withLoadingHOC';
import WealthieTable from '../../components/WelthieTable';

const defaultStartDate = moment().subtract(1, 'months').format('DD/MM/YYYY');
const defaultEndDate = moment().format('DD/MM/YYYY');

type UserDetalParam = {
  id: string;
  parentId: string;
};

function UserDetail(): React.ReactElement {
  const [form] = Form.useForm();
  const history = useHistory();
  const dispatch = useDispatch();
  const { id, parentId } = useParams<UserDetalParam>();
  const currentChild = useSelector((state: RootState) =>
    selectCurrentChild(state),
  );
  const [isDeleting, setDeleting] = useState<boolean>(false);

  const breadcrumbConfig = useMemo(
    () => [
      {
        name: 'Wealthie',
      },
      {
        name: 'User Detail',
        link: `/users/${parentId}`,
      },
      {
        name: 'Children',
      },
    ],
    [parentId],
  );

  const giftCollumns = useMemo(
    () => [
      {
        title: 'Amount',
        dataIndex: 'giftcard_amt',
        key: 'giftcard_amt',
      },
      {
        title: 'Sender Name',
        dataIndex: 'sender_name',
        key: 'sender_name',
      },
      {
        title: 'Internal Id',
        dataIndex: 'id',
        key: 'id',
        render: function renderGiftId(text: string) {
          return <a>{text}</a>;
        },
      },
    ],
    [],
  );

  const logCollumns = useMemo(
    () => [
      {
        title: 'Action Timestamp',
        dataIndex: 'action_timestamp',
        key: 'action_timestamp',
      },
      {
        title: 'Action Description',
        dataIndex: 'action_description',
        key: 'action_description',
      },
      {
        title: 'Action Notes',
        dataIndex: ' action_notes',
        key: 'action_notes',
      },
    ],
    [],
  );

  useEffect(() => {
    dispatch(getCurrentChild(id, parentId));
  }, [dispatch, id, parentId]);

  // inital values form
  useEffect(() => {
    const initalValues = {
      firstName: currentChild?.first_name,
      lastName: currentChild?.last_name,
      dobr: currentChild?.date_of_birth
        ? moment(new Date(currentChild?.date_of_birth), 'DD/MM/YYYY').utc(false)
        : moment(moment(), 'DD/MM/YYYY').utc(false),
      username: currentChild?.cognito_user_id,
      notes: currentChild?.notes,
    };
    form.setFieldsValue(initalValues);
  }, [currentChild, form]);

  const onFinish = useCallback(
    ({ firstName, lastName, dobr, notes }) => {
      dispatch(
        updateChild(id, parentId, {
          firstName,
          lastName,
          dob: dobr.format('DD/MM/YYYY'),
          notes,
        }),
      );
    },
    [dispatch, id, parentId],
  );

  const onFinishFailed = useCallback(() => {
    showNotification({
      type: ERROR_TYPE,
      message: 'Please fill in proper form values.',
    });
  }, []);

  const confirmDeleteUser = useCallback(async () => {
    try {
      setDeleting(true);
      await deleteParent(id);
      showNotification({
        type: 'success',
        message: 'Delete user successfully!',
      });
      setDeleting(false);
      history.push('/users');
    } catch (error) {
      setDeleting(false);
      showNotification({
        type: 'error',
        message: 'Somethings went wrong, please try again',
      });
    }
  }, [id, history]);

  return (
    <>
      <WealthieBreadCrumb breadcrumbConfig={breadcrumbConfig} />
      <div data-testid="child_detail_page" className={styles.wrapper}>
        <div className={styles.edit_profile_wrapper}>
          <Form
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            form={form}
            validateMessages={VALIDATE_MESSAGES}
            {...LAYOUT_0_24}
            data-testid="edit_profile_form"
          >
            <Form.Item label="Username" name="username">
              <Input
                disabled
                aria-label="email-input"
                placeholder="Your Username"
              />
            </Form.Item>
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[{ required: true }]}
            >
              <Input aria-label="fName-input" placeholder="Your First Name" />
            </Form.Item>
            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[{ required: true }]}
            >
              <Input aria-label="lName-input" placeholder="Your Last Name" />
            </Form.Item>
            <Form.Item label="Date" name="dobr" rules={[{ required: true }]}>
              <AntdDatePicker
                aria-label="dob-input"
                placeholder="Your Date Of Birth (DD/MM/YYYY)"
              />
            </Form.Item>
            <Form.Item label="Notes" name="notes" rules={[{ required: false }]}>
              <Input aria-label="notes-input" placeholder="Your notes" />
            </Form.Item>
            <Form.Item className={styles.user_action_wrapper}>
              <Button
                type="primary"
                htmlType="submit"
                data-testid="save_button"
                className={styles.save_button}
              >
                Save
              </Button>
              {/* TODO: add condition to show or hide this button */}
              <Popconfirm
                onConfirm={confirmDeleteUser}
                title="Do you want delete this user?"
              >
                <Button loading={isDeleting} danger>
                  Delete
                </Button>
              </Popconfirm>
            </Form.Item>
          </Form>
          <div>Giftcards</div>
          <WealthieTable
            extraApiParams={[parentId, id]}
            rowKey="id"
            api={getGiftApi}
            filterData="data.data"
            filterPagination="data.pagination.totalCount"
            columns={giftCollumns}
            onChangeCallBack={undefined}
          />
          <div>Log Entries</div>
          <WealthieTable
            rowKey="id"
            api={getLogEntriesApi}
            columns={logCollumns}
            extraApiParams={[defaultStartDate, defaultEndDate, parentId, id]}
            onChangeCallBack={undefined}
          />
        </div>
      </div>
    </>
  );
}

export default withLoading(UserDetail);
